import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  IconButton,
  Box,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  useToast,
  FormControl,
  FormLabel,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
} from "@chakra-ui/react";
import { FaTrash, FaUsers } from "react-icons/fa";
import { useGame } from "../../contexts/GameContext";
import { EventTypes, GameEvent } from "../../types";

interface PlayerManagementProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PlayerManagement: React.FC<PlayerManagementProps> = ({
  isOpen,
  onClose,
}) => {
  const { gameState, sendEvent, currentPlayer } = useGame();
  const [playerToRemove, setPlayerToRemove] = useState<string | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const handleRemovePlayer = async (playerName: string) => {
    try {
      await sendEvent({
        type: EventTypes.REMOVE_PLAYER,
        playerName,
      });
      toast({
        title: "Player removed",
        description: `${playerName} has been removed from the game`,
        status: "success",
        duration: 3000,
      });
      setPlayerToRemove(null);
    } catch (error) {
      toast({
        title: "Failed to remove player",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  if (!gameState) return null;

  const sortedPlayers = [...gameState.players].sort((a, b) => b.score - a.score);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent bg="jeopardy.blue" mx={4}>
          <ModalHeader>
            <HStack>
              <FaUsers />
              <Text textStyle="clue">Player Management</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pb={6}>
            <VStack spacing={4} width="100%">
              {/* Max Players Control */}
              <Box width="100%" bg="rgba(255,255,255,0.1)" p={4} borderRadius="md">
                <FormControl>
                  <HStack justify="space-between" width="100%">
                    <FormLabel htmlFor="max-players-toggle" mb="0" textStyle="clue" fontSize="sm">
                      Limit Players
                    </FormLabel>
                    <Switch
                      id="max-players-toggle"
                      isChecked={gameState.maxPlayers != null}
                      onChange={async (e) => {
                        try {
                          await sendEvent({
                            type: EventTypes.SET_MAX_PLAYERS,
                            maxPlayers: e.target.checked
                              ? Math.max(gameState.players.length, 2)
                              : null,
                          });
                        } catch (error) {
                          toast({
                            title: "Failed to update player limit",
                            description: error instanceof Error ? error.message : "An error occurred",
                            status: "error",
                            duration: 3000,
                          });
                        }
                      }}
                      colorScheme="yellow"
                    />
                  </HStack>
                </FormControl>

                {gameState.maxPlayers != null && (
                  <FormControl mt={3}>
                    <HStack justify="space-between" width="100%">
                      <Text textStyle="clue" fontSize="sm" opacity={0.8}>
                        Max: {gameState.players.length}/{gameState.maxPlayers}
                      </Text>
                      <NumberInput
                        value={gameState.maxPlayers}
                        onChange={async (_, val) => {
                          if (val && val >= gameState.players.length && val <= 20) {
                            try {
                              await sendEvent({
                                type: EventTypes.SET_MAX_PLAYERS,
                                maxPlayers: val,
                              });
                            } catch (error) {
                              toast({
                                title: "Failed to update player limit",
                                description: error instanceof Error ? error.message : "An error occurred",
                                status: "error",
                                duration: 3000,
                              });
                            }
                          }
                        }}
                        min={Math.max(gameState.players.length, 2)}
                        max={20}
                        size="sm"
                        width="100px"
                      >
                        <NumberInputField bg="white" color="black" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </HStack>
                  </FormControl>
                )}
              </Box>

              {/* Audience Only Mode */}
              <Box width="100%" bg="rgba(255,255,255,0.1)" p={4} borderRadius="md">
                <FormControl>
                  <HStack justify="space-between" width="100%">
                    <VStack align="start" spacing={0}>
                      <FormLabel htmlFor="audience-only-toggle" mb="0" textStyle="clue" fontSize="sm">
                        Audience Only Mode
                      </FormLabel>
                      <Text textStyle="clue" fontSize="xs" opacity={0.6}>
                        QR code goes to audience view only
                      </Text>
                    </VStack>
                    <Switch
                      id="audience-only-toggle"
                      isChecked={gameState.audienceOnly ?? false}
                      onChange={async (e) => {
                        try {
                          await sendEvent({
                            type: EventTypes.SET_AUDIENCE_ONLY,
                            audienceOnly: e.target.checked,
                          });
                        } catch (error) {
                          toast({
                            title: "Failed to update mode",
                            description: error instanceof Error ? error.message : "An error occurred",
                            status: "error",
                            duration: 3000,
                          });
                        }
                      }}
                      colorScheme="yellow"
                    />
                  </HStack>
                </FormControl>
              </Box>

              {/* Fair Buzzing Mode */}
              <Box width="100%" bg="rgba(255,255,255,0.1)" p={4} borderRadius="md">
                <FormControl>
                  <HStack justify="space-between" width="100%">
                    <VStack align="start" spacing={0}>
                      <FormLabel htmlFor="fair-buzzing-toggle" mb="0" textStyle="clue" fontSize="sm">
                        Fair Buzzing Mode
                      </FormLabel>
                      <Text textStyle="clue" fontSize="xs" opacity={0.6}>
                        Everyone buzzes once before anyone can buzz again
                      </Text>
                    </VStack>
                    <Switch
                      id="fair-buzzing-toggle"
                      isChecked={gameState.fairBuzzingEnabled ?? false}
                      onChange={async (e) => {
                        try {
                          await sendEvent({
                            type: EventTypes.SET_FAIR_BUZZING,
                            enabled: e.target.checked,
                          });
                          toast({
                            title: e.target.checked ? "Fair Buzzing enabled" : "Fair Buzzing disabled",
                            description: e.target.checked 
                              ? "Players must take turns buzzing in" 
                              : "Normal buzzing restored",
                            status: "success",
                            duration: 2000,
                          });
                        } catch (error) {
                          toast({
                            title: "Failed to update mode",
                            description: error instanceof Error ? error.message : "An error occurred",
                            status: "error",
                            duration: 3000,
                          });
                        }
                      }}
                      colorScheme="yellow"
                    />
                  </HStack>
                </FormControl>
              </Box>

              <Divider borderColor="rgba(255,255,255,0.2)" />

              {sortedPlayers.length === 0 ? (
                <Box
                  width="100%"
                  bg="rgba(255,255,255,0.1)"
                  p={4}
                  borderRadius="md"
                  textAlign="center"
                >
                  <Text textStyle="clue" opacity={0.7}>
                    No players have joined yet
                  </Text>
                </Box>
              ) : (
                sortedPlayers.map((player, index) => {
                  const isCurrentPlayer = currentPlayer?.name === player.name;
                  const isActivePlayer = gameState.activePlayer?.name === player.name;

                  return (
                    <HStack
                      key={player.name}
                      width="100%"
                      bg={
                        isCurrentPlayer
                          ? "rgba(255, 215, 0, 0.2)"
                          : "rgba(255,255,255,0.1)"
                      }
                      p={3}
                      borderRadius="md"
                      justifyContent="space-between"
                      border={isActivePlayer ? "2px solid" : "none"}
                      borderColor={isActivePlayer ? "jeopardy.gold" : "transparent"}
                    >
                      <HStack spacing={3} flex={1}>
                        <Box
                          bg="jeopardy.gold"
                          color="black"
                          width="28px"
                          height="28px"
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="sm"
                        >
                          {index + 1}
                        </Box>
                        <VStack align="start" spacing={0} flex={1}>
                          <HStack>
                            <Text textStyle="clue" fontWeight="bold">
                              {player.name}
                            </Text>
                            {isCurrentPlayer && (
                              <Text
                                textStyle="clue"
                                fontSize="xs"
                                color="jeopardy.gold"
                              >
                                (You)
                              </Text>
                            )}
                            {isActivePlayer && (
                              <Text
                                textStyle="clue"
                                fontSize="xs"
                                color="jeopardy.gold"
                              >
                                (Host)
                              </Text>
                            )}
                          </HStack>
                          <Text textStyle="clue" fontSize="sm" opacity={0.7}>
                            ${player.score}
                          </Text>
                        </VStack>
                      </HStack>
                      <IconButton
                        aria-label={`Remove ${player.name}`}
                        icon={<FaTrash />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => setPlayerToRemove(player.name)}
                        isDisabled={isCurrentPlayer}
                        _hover={{ bg: "red.600" }}
                      />
                    </HStack>
                  );
                })
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={!!playerToRemove}
        leastDestructiveRef={cancelRef}
        onClose={() => setPlayerToRemove(null)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="jeopardy.blue" mx={4}>
            <AlertDialogHeader>
              <Text textStyle="clue">Remove Player?</Text>
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text textStyle="clue">
                Are you sure you want to remove{" "}
                <Text as="span" fontWeight="bold" color="jeopardy.gold">
                  {playerToRemove}
                </Text>
                ? This action cannot be undone.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setPlayerToRemove(null)}
                bg="jeopardy.lightBlue"
              >
                <Text textStyle="clue">Cancel</Text>
              </Button>
              <Button
                colorScheme="red"
                onClick={() => playerToRemove && handleRemovePlayer(playerToRemove)}
                ml={3}
              >
                <Text textStyle="clue">Remove</Text>
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
